import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(false);
  const [lightMode, setLightMode] = useState(false);
  
  useEffect(() => {
    // Check for user preference on initial load
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Check for explicit light mode setting
    const savedLightMode = localStorage.getItem('lightMode') === 'true';
    setLightMode(savedLightMode);
    
    if (savedLightMode) {
      document.documentElement.classList.add('light');
    }
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);
  
  const toggleDarkMode = () => {
    setDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      
      if (newMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      
      return newMode;
    });
  };
  
  const toggleLightMode = () => {
    setLightMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('lightMode', newMode.toString());
      
      if (newMode) {
        document.documentElement.classList.add('light');
        // When enabling light mode, make sure dark mode is removed
        document.documentElement.classList.remove('dark');
        setDarkMode(false);
        localStorage.setItem('theme', 'light');
      } else {
        document.documentElement.classList.remove('light');
        // When disabling light mode, restore dark mode based on preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.documentElement.classList.add('dark');
          setDarkMode(true);
          localStorage.setItem('theme', 'dark');
        }
      }
      
      return newMode;
    });
  };
  
  const value = {
    darkMode,
    toggleDarkMode,
    lightMode,
    toggleLightMode
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeContext; 